import {useCallback, useEffect, useRef, useState} from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CookiePreferencesDialog from './CookiePreferencesDialog';
import {
    applyConsent,
    getRegionModel,
    getStoredConsent,
    hasUniversalOptOutSignal,
    saveConsent,
    OPEN_COOKIE_SETTINGS_EVENT,
    PRIVACY_POLICY_URL,
} from 'consent/consentManager';

/**
 * Site-wide cookie consent UI: first-visit banner and granular preferences
 * dialog. Mounted once in views/base/Layout.js; reopened anytime via
 * openCookieSettings() (footer "Cookie Settings" link).
 *
 * The banner is compact so it does not sit on a page's first-screen content
 * (a hero CTA, a demo): on phones a short sheet — the two choices side by
 * side, "Manage preferences" as a link under them — and from tablet up a
 * narrow card in the bottom-LEFT corner (the chat widget owns the right one)
 * instead of a wide bar across the middle. The choices themselves are
 * untouched: Accept and Reject are the same size and style, side by side,
 * and an opt-in region still gets no way to close it without choosing.
 */
const CookieConsent = () => {
    const [bannerOpen, setBannerOpen] = useState(false);
    const [prefsOpen, setPrefsOpen] = useState(false);
    const isOptOutRegion = getRegionModel() === 'optout';

    // While the banner is up, the page knows how tall it is: <html> carries
    // .has-cookie-banner and --cookie-banner-h, so whatever else is fixed to
    // the bottom edge (the chat bubble, a page's sticky CTA) can sit above it
    // instead of on top of its buttons. See base.css.
    const bannerRef = useRef(null);
    useEffect(() => {
        const root = document.documentElement;
        const el = bannerRef.current;
        if (!bannerOpen || !el) return undefined;
        const publish = () => root.style.setProperty('--cookie-banner-h', `${Math.ceil(el.getBoundingClientRect().height)}px`);
        publish();
        root.classList.add('has-cookie-banner');
        const observer = 'ResizeObserver' in window ? new ResizeObserver(publish) : null;
        if (observer) observer.observe(el);
        window.addEventListener('resize', publish);
        return () => {
            if (observer) observer.disconnect();
            window.removeEventListener('resize', publish);
            root.classList.remove('has-cookie-banner');
            root.style.removeProperty('--cookie-banner-h');
        };
    }, [bannerOpen]);

    useEffect(() => {
        // Returning visitors (or US defaults): apply stored/effective consent
        // so gtag consent-mode state matches and granted scripts load.
        applyConsent();
        setBannerOpen(!getStoredConsent());

        const handleOpenSettings = () => setPrefsOpen(true);
        window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
        return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
    }, []);

    const decide = useCallback((categories, method) => {
        saveConsent(categories, method);
        setBannerOpen(false);
        setPrefsOpen(false);
    }, []);

    const handleAcceptAll = () => decide({analytics: true, marketing: true}, 'accept_all');
    const handleRejectAll = () => decide({analytics: false, marketing: false}, 'reject_all');
    const handleSave = (toggles) => decide(toggles, 'preferences');
    // US (opt-out model) only: closing the banner keeps the region defaults
    // and records the notice was shown — unless a DNT/GPC browser signal is
    // present, which counts as an opt-out. Never rendered for opt-in regions —
    // there, closing must not grant anything, so no close control exists.
    const handleDismiss = () => {
        const defaultsOn = !hasUniversalOptOutSignal();
        decide({analytics: defaultsOn, marketing: defaultsOn}, 'dismissed');
    };

    return (
        <>
            {bannerOpen && (
                <Paper
                    ref={bannerRef}
                    role="region"
                    aria-label="Cookie consent"
                    elevation={8}
                    sx={{
                        position: 'fixed',
                        bottom: {xs: 0, sm: 16},
                        left: {xs: 0, sm: 16},
                        width: {xs: '100%', sm: 400},
                        maxWidth: {sm: 'calc(100vw - 32px)'},
                        zIndex: (theme) => theme.zIndex.modal - 1,
                        p: {xs: 1.5, sm: 2},
                        pb: {xs: 'calc(12px + env(safe-area-inset-bottom))', sm: 2},
                        borderRadius: {xs: '12px 12px 0 0', sm: 2},
                    }}
                >
                    <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                        <Typography variant="body2" sx={{flex: 1, fontSize: 13, lineHeight: 1.45}}>
                            {isOptOutRegion ? (
                                <>
                                    We use cookies for analytics and advertising. You can opt out at any
                                    time — see our{' '}
                                    <Link href={PRIVACY_POLICY_URL} target="_blank" rel="noopener">Privacy policy</Link>.{' '}
                                    <Link component="button" type="button" variant="body2" onClick={() => setPrefsOpen(true)} sx={{verticalAlign: 'baseline'}}>
                                        Do Not Sell or Share My Personal Information
                                    </Link>
                                </>
                            ) : (
                                <>
                                    We use cookies to run this site and, with your permission, for
                                    analytics and advertising. Non-essential cookies stay off until you
                                    accept them, and you can change your choice at any time. See our{' '}
                                    <Link href={PRIVACY_POLICY_URL} target="_blank" rel="noopener">Privacy policy</Link>.
                                </>
                            )}
                        </Typography>
                        {isOptOutRegion && (
                            <IconButton size="small" aria-label="Close cookie notice" onClick={handleDismiss}>
                                <CloseIcon fontSize="small"/>
                            </IconButton>
                        )}
                    </Box>
                    {/* the two choices share a row at every width, equal in size and
                        style; "Manage preferences" is the quieter third option under them */}
                    <Stack direction="row" spacing={1} sx={{mt: 1.5}}>
                        <Button variant="contained" size="small" onClick={handleRejectAll} sx={{flex: 1}}>
                            Reject all
                        </Button>
                        <Button variant="contained" size="small" onClick={handleAcceptAll} sx={{flex: 1}}>
                            Accept all
                        </Button>
                    </Stack>
                    <Button variant="text" size="small" onClick={() => setPrefsOpen(true)} sx={{mt: 0.5, width: '100%'}}>
                        Manage preferences
                    </Button>
                </Paper>
            )}

            <CookiePreferencesDialog
                open={prefsOpen}
                onClose={() => setPrefsOpen(false)}
                onSave={handleSave}
                onAcceptAll={handleAcceptAll}
                onRejectAll={handleRejectAll}
            />
        </>
    );
};

export default CookieConsent;
